import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { type BatchView, getBatch, message } from "./releaseClient";

/** One batch: manifest, preflight, population, receipts, and reconciliation. */
export function ReleaseBatchPage() {
  const { batchId = "" } = useParams();
  const [view, setView] = useState<BatchView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getBatch(batchId)
      .then((found) => {
        if (current) {
          setView(found);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (current) {
          setError(message(caught));
        }
      });
    return () => {
      current = false;
    };
  }, [batchId]);

  if (error) {
    return <p role="alert">Could not load: {error}</p>;
  }
  if (!view) {
    return <p>Loading batch.</p>;
  }
  const defects = view.verification.filter((row) => row.outcome === "unexpected_present");
  return (
    <section aria-labelledby="batch-title">
      <p>
        <Link to="/release-center">All batches</Link>
      </p>
      <h1 id="batch-title">{view.batch.batch_key}</h1>
      <p>
        {view.batch.release_state}, for {view.batch.purpose}, through {view.batch.interface}.
      </p>
      {defects.length > 0 && (
        <p role="alert">
          Release defect: {defects.length} held-back values were found in the destination.
        </p>
      )}

      <h2>Manifest</h2>
      {view.manifest_sha256 ? (
        <p>
          Version {view.manifest_version}: <code>{view.manifest_sha256}</code>
        </p>
      ) : (
        <p>No manifest yet.</p>
      )}

      <h2>Preflight</h2>
      {view.preflight.length === 0 ? (
        <p>Not run yet.</p>
      ) : (
        <ul aria-label="Preflight">
          {view.preflight.map((check) => (
            <li key={check.check_code}>
              {check.check_code}: {check.outcome}. {check.reason}
            </li>
          ))}
        </ul>
      )}

      <h2>Population</h2>
      <dl aria-label="Population">
        {Object.entries(view.population).map(([name, count]) => (
          <div key={name}>
            <dt>{name}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>

      <h2>Receipts</h2>
      <ul aria-label="Receipts">
        {view.receipts.statuses.map((item) => (
          <li key={item.release_item_id}>
            Item {item.item_seq} ({item.target_object}): {item.outcome}
            {item.destination_id && `, destination ${item.destination_id}`}
            {item.error && `, ${item.error}`}
            {item.value_changed && ", value changed by the destination"}
          </li>
        ))}
      </ul>

      <h2>Reconciliation</h2>
      {view.verification.length === 0 ? (
        <p>Not reconciled yet.</p>
      ) : (
        <ul aria-label="Reconciliation">
          {view.verification.map((row, index) => (
            <li key={`${row.release_item_id ?? row.holdback_id}-${index}`}>
              {row.verification_method}: {row.outcome}
              {row.details.detail && `. ${row.details.detail}`}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
